import React, { useEffect, useState } from "react";
import api from "../../services/api";
import {
  PageContainer, PageHeader,
  Button, Input, Muted,
} from "../ui";

interface Format {
  id: string;
  name: string;
  package_type: string;
  cash_price: number;
  card_price: number;
  oz_per_unit: number;
  units_per_case: number;
  upc: string;
}

interface Product {
  id: string;
  name: string;
  brand: string;
  category: string;
  case_configuration: string;
  case_sku: string;
  sellable_formats: Format[];
}

type Template = "combined" | "shelf" | "cooler" | "endcap" | "draft_menu";

// Which price breakdowns to show on the tag
interface TagOptions {
  showCase: boolean;
  showCasePack: boolean;  // 12-pack, 6-pack
  showSingle: boolean;
  showBarcode: boolean;
}

export default function ShelfTags() {
  const [products, setProducts] = useState<Product[]>([]);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [template, setTemplate] = useState<Template>("combined");
  const [search, setSearch] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [vendorFilter, setVendorFilter] = useState("");
  const [showPreview, setShowPreview] = useState(false);
  const [vendors, setVendors] = useState<Array<{ id: string; name: string }>>([]);
  const [tagOptions, setTagOptions] = useState<TagOptions>({
    showCase: true,
    showCasePack: true,
    showSingle: true,
    showBarcode: true,
  });

  useEffect(() => {
    api.get("/products?is_active=true").then(r => setProducts(r.data));
    api.get("/vendors").then(r => setVendors(r.data)).catch(() => {});
  }, []);

  const toggleProduct = (id: string) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelected(next);
  };

  const filteredProducts = products.filter((p) => {
    if (search && !p.name.toLowerCase().includes(search.toLowerCase()) && !(p.brand && p.brand.toLowerCase().includes(search.toLowerCase()))) return false;
    if (categoryFilter && p.category !== categoryFilter) return false;
    if (vendorFilter) {
      const pAny = p as unknown as Record<string, unknown>;
      if (pAny.vendor_id && pAny.vendor_id !== vendorFilter) return false;
    }
    return true;
  });

  const selectedProducts = products.filter((p) => selected.has(p.id));

  const selectAll = () => {
    if (selected.size === filteredProducts.length) setSelected(new Set());
    else setSelected(new Set(filteredProducts.map(p => p.id)));
  };

  if (showPreview) {
    return (
      <div>
        <div className="print:hidden p-4 flex gap-3" style={{ background: "var(--rbw-bg)" }}>
          <Button onClick={() => setShowPreview(false)}>Back</Button>
          <Button onClick={() => window.print()}>Print</Button>
          <span className="text-sm self-center" style={{ color: "var(--rbw-gray-dark)" }}>
            {selectedProducts.length} tags
          </span>
        </div>
        <div className="p-4">
          {template === "draft_menu" ? (
            <DraftMenuPreview products={selectedProducts} />
          ) : (
            <div className={`grid gap-2 ${template === "endcap" ? "grid-cols-2" : "grid-cols-3"}`}
              style={{ gridAutoRows: "min-content" }}>
              {selectedProducts.map(product => (
                <CombinedTagCard
                  key={product.id}
                  product={product}
                  template={template}
                  options={tagOptions}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <PageContainer>
      <PageHeader
        title="Shelf Tags"
        subtitle={`${selected.size} selected for printing`}
        action={
          <Button onClick={() => setShowPreview(true)} disabled={selected.size === 0}>
            Preview & Print ({selected.size})
          </Button>
        }
      />

      {/* Tag options */}
      <div className="rounded-xl p-4 mb-4 flex flex-wrap gap-6 items-center"
        style={{ background: "var(--rbw-bg-card)", border: "1px solid var(--rbw-border)" }}>
        <div>
          <label className="text-xs font-bold uppercase block mb-1" style={{ color: "var(--rbw-gray-dark)" }}>Template</label>
          <select value={template} onChange={e => setTemplate(e.target.value as Template)}
            className="px-3 py-2 rounded-lg text-sm" style={{ border: "1px solid var(--rbw-border)" }}>
            <option value="combined">Combined Tag (all prices)</option>
            <option value="shelf">Shelf Tag (3.5" x 1.25")</option>
            <option value="cooler">Cooler Tag (4" x 2")</option>
            <option value="endcap">Endcap Promo (5" x 3")</option>
            <option value="draft_menu">Draft Menu (Full Page)</option>
          </select>
        </div>

        <div className="flex gap-4">
          <label className="text-xs font-bold uppercase block" style={{ color: "var(--rbw-gray-dark)" }}>
            Show on tag:
          </label>
          <label className="flex items-center gap-1.5 text-sm cursor-pointer">
            <input type="checkbox" checked={tagOptions.showCase}
              onChange={e => setTagOptions({ ...tagOptions, showCase: e.target.checked })} />
            <span>Case</span>
          </label>
          <label className="flex items-center gap-1.5 text-sm cursor-pointer">
            <input type="checkbox" checked={tagOptions.showCasePack}
              onChange={e => setTagOptions({ ...tagOptions, showCasePack: e.target.checked })} />
            <span>Case Pack</span>
          </label>
          <label className="flex items-center gap-1.5 text-sm cursor-pointer">
            <input type="checkbox" checked={tagOptions.showSingle}
              onChange={e => setTagOptions({ ...tagOptions, showSingle: e.target.checked })} />
            <span>Single</span>
          </label>
          <label className="flex items-center gap-1.5 text-sm cursor-pointer">
            <input type="checkbox" checked={tagOptions.showBarcode}
              onChange={e => setTagOptions({ ...tagOptions, showBarcode: e.target.checked })} />
            <span>UPC Barcode</span>
          </label>
        </div>
      </div>

      {/* Search and filters */}
      <div className="flex gap-3 mb-4">
        <div className="flex-1">
          <Input value={search} onChange={setSearch} placeholder="Search products..." />
        </div>
        <select value={categoryFilter} onChange={e => { setCategoryFilter(e.target.value); setSelected(new Set()); }}
          className="px-3 py-2 rounded-lg text-sm" style={{ border: "1px solid var(--rbw-border)" }}>
          <option value="">All Categories</option>
          {["domestic", "import", "craft", "seltzer", "rtd", "malternative", "cider", "na", "accessory"].map(c =>
            <option key={c} value={c}>{c}</option>
          )}
        </select>
        <Button onClick={selectAll}>
          {selected.size === filteredProducts.length ? "Deselect All" : `Select All (${filteredProducts.length})`}
        </Button>
      </div>

      <div className="rounded-2xl shadow-sm overflow-hidden" style={{ background: "var(--rbw-bg-card)", border: "1px solid var(--rbw-border)" }}>
        {filteredProducts.map((product, idx) => {
          const formats = typeof product.sellable_formats === "string"
            ? JSON.parse(product.sellable_formats) : product.sellable_formats || [];

          return (
            <div key={product.id}
              className="flex items-center px-5 py-3 cursor-pointer transition-all"
              style={{
                borderBottom: "1px solid var(--rbw-border)",
                background: selected.has(product.id) ? "#8BB8E815" : idx % 2 === 0 ? "transparent" : "var(--rbw-bg)",
              }}
              onClick={() => toggleProduct(product.id)}>
              <input type="checkbox" checked={selected.has(product.id)} onChange={() => toggleProduct(product.id)} className="mr-3" />
              <div className="flex-1">
                <div className="font-medium text-sm" style={{ color: "var(--rbw-navy)" }}>{product.name}</div>
                <Muted>{product.category} | {product.case_configuration}</Muted>
              </div>
              <div className="text-right text-xs">
                {formats.map((f: Format) => (
                  <span key={f.id} className="inline-block px-2 py-0.5 rounded mr-1 mb-0.5"
                    style={{ background: "var(--rbw-gray-light)", color: "var(--rbw-navy)" }}>
                    {f.name}: ${Number(f.cash_price).toFixed(2)}
                  </span>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </PageContainer>
  );
}

// ── Combined Tag: shows all selected price breakdowns on one tag ──

function CombinedTagCard({ product, template, options }: {
  product: Product;
  template: string;
  options: TagOptions;
}) {
  const formats = typeof product.sellable_formats === "string"
    ? JSON.parse(product.sellable_formats) as Format[]
    : (product.sellable_formats || []) as Format[];

  const caseFormat = formats.find(f => f.package_type === "case_full");
  const packFormats = formats.filter(f => ["12pk", "8pk", "6pk", "4pk"].includes(f.package_type));
  const singleFormat = formats.find(f => f.package_type === "single");
  const primaryUpc = formats.find(f => f.upc && f.upc.length >= 10)?.upc || product.case_sku || "";

  // Clean product name (remove case config from display name)
  const displayName = product.name
    .replace(/\s*\d+\/\d+\s*\d*\.?\d*\s*(OZ|oz).*$/i, "")
    .replace(/\s*\d+\s*(OZ|oz).*$/i, "")
    .toUpperCase();

  const sizes: Record<string, { width: string; minHeight: string }> = {
    combined: { width: "3.8in", minHeight: "2.2in" },
    shelf: { width: "3.5in", minHeight: "1.25in" },
    cooler: { width: "4in", minHeight: "2in" },
    endcap: { width: "5in", minHeight: "3in" },
  };
  const size = sizes[template] || sizes.combined;

  const pricesToShow: Array<{ label: string; config: string; cash: number; card: number }> = [];

  if (options.showCase && caseFormat) {
    pricesToShow.push({
      label: "CASE",
      config: product.case_configuration || "Full Case",
      cash: Number(caseFormat.cash_price),
      card: Number(caseFormat.card_price),
    });
  }

  if (options.showCasePack) {
    for (const fmt of packFormats) {
      pricesToShow.push({
        label: ({ "12pk": "12-PACK", "8pk": "8-PACK", "6pk": "6-PACK", "4pk": "4-PACK" } as Record<string, string>)[fmt.package_type] || fmt.package_type.toUpperCase(),
        config: fmt.name,
        cash: Number(fmt.cash_price),
        card: Number(fmt.card_price),
      });
    }
  }

  if (options.showSingle && singleFormat) {
    pricesToShow.push({
      label: "SINGLE",
      config: singleFormat.name,
      cash: Number(singleFormat.cash_price),
      card: Number(singleFormat.card_price),
    });
  }

  // If nothing selected, show all available
  if (pricesToShow.length === 0) {
    for (const fmt of formats) {
      pricesToShow.push({
        label: fmt.name.toUpperCase(),
        config: fmt.name,
        cash: Number(fmt.cash_price),
        card: Number(fmt.card_price),
      });
    }
  }

  return (
    <div className="rounded p-2.5 flex flex-col justify-between"
      style={{
        width: size.width,
        minHeight: size.minHeight,
        border: "1.5px solid #333",
        pageBreakInside: "avoid",
        fontSize: template === "endcap" ? "12px" : "10px",
      }}>
      {/* Product name */}
      <div style={{ borderBottom: "1px solid #ccc", paddingBottom: "3px", marginBottom: "4px" }}>
        <div className="font-bold leading-tight" style={{ fontSize: template === "endcap" ? "16px" : "13px" }}>
          {displayName}
        </div>
        <div style={{ color: "#666", fontSize: "9px" }}>
          {product.category.toUpperCase()} | {product.case_configuration}
        </div>
      </div>

      {/* Price grid */}
      <div className="flex-1">
        <table className="w-full" style={{ borderCollapse: "collapse" }}>
          <thead>
            <tr>
              <th style={{ textAlign: "left", fontSize: "8px", color: "#999", fontWeight: "normal", paddingBottom: "2px" }}></th>
              <th style={{ textAlign: "right", fontSize: "8px", color: "#B8860B", fontWeight: "bold", paddingBottom: "2px" }}>CASH</th>
              <th style={{ textAlign: "right", fontSize: "8px", color: "#4A7FB5", fontWeight: "bold", paddingBottom: "2px" }}>CARD</th>
            </tr>
          </thead>
          <tbody>
            {pricesToShow.map((p, i) => (
              <tr key={i} style={{ borderBottom: i < pricesToShow.length - 1 ? "1px dotted #ddd" : "none" }}>
                <td style={{ paddingTop: "2px", paddingBottom: "2px" }}>
                  <span className="font-bold" style={{ fontSize: template === "endcap" ? "11px" : "9px" }}>{p.label}</span>
                </td>
                <td style={{ textAlign: "right", paddingTop: "2px", paddingBottom: "2px" }}>
                  <span className="font-bold" style={{
                    fontSize: template === "endcap" ? "18px" : pricesToShow.length <= 2 ? "16px" : "13px",
                    color: "#B8860B",
                  }}>
                    ${p.cash.toFixed(2)}
                  </span>
                </td>
                <td style={{ textAlign: "right", paddingTop: "2px", paddingBottom: "2px" }}>
                  <span style={{
                    fontSize: template === "endcap" ? "14px" : pricesToShow.length <= 2 ? "13px" : "11px",
                    color: "#4A7FB5",
                  }}>
                    ${p.card.toFixed(2)}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* UPC barcode area */}
      {options.showBarcode && primaryUpc && (
        <div style={{ borderTop: "1px solid #ccc", paddingTop: "3px", marginTop: "4px", textAlign: "center" }}>
          {/* Barcode representation using CSS (Code 128 style bars) */}
          <div style={{ fontFamily: "'Libre Barcode 128', monospace", fontSize: "32px", lineHeight: 1, letterSpacing: "0" }}>
            {primaryUpc}
          </div>
          <div style={{ fontSize: "8px", color: "#666", fontFamily: "monospace", letterSpacing: "1px" }}>
            {primaryUpc}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Draft Menu (unchanged) ──

function DraftMenuPreview({ products }: { products: Product[] }) {
  const kegProducts = products.filter((p) => {
    const formats = typeof p.sellable_formats === "string" ? JSON.parse(p.sellable_formats) : p.sellable_formats || [];
    return formats.some((f: { package_type: string }) => ["growler", "crowler", "pint", "taster"].includes(f.package_type));
  });

  return (
    <div className="w-[8.5in] min-h-[11in] mx-auto p-8" style={{ background: "#fff", border: "1px solid var(--rbw-border)" }}>
      <h1 className="text-3xl font-bold text-center mb-2" style={{ color: "var(--rbw-navy)" }}>DRAFT & POUR MENU</h1>
      <p className="text-center mb-6" style={{ color: "var(--rbw-gray-dark)" }}>Rieker Bottle Works</p>
      <table className="w-full">
        <thead>
          <tr style={{ borderBottom: "2px solid var(--rbw-navy)" }}>
            <th className="text-left py-2 text-lg" style={{ color: "var(--rbw-navy)" }}>Beer</th>
            <th className="text-center py-2" style={{ color: "var(--rbw-navy)" }}>Taster</th>
            <th className="text-center py-2" style={{ color: "var(--rbw-navy)" }}>Pint</th>
            <th className="text-center py-2" style={{ color: "var(--rbw-navy)" }}>Crowler</th>
            <th className="text-center py-2" style={{ color: "var(--rbw-navy)" }}>Growler</th>
          </tr>
        </thead>
        <tbody>
          {kegProducts.map((product) => {
            const formats = typeof product.sellable_formats === "string"
              ? JSON.parse(product.sellable_formats) : product.sellable_formats || [];
            const getPrice = (type: string) => {
              const fmt = formats.find((f: { package_type: string }) => f.package_type === type);
              return fmt ? `$${Number(fmt.cash_price).toFixed(2)}` : "--";
            };
            return (
              <tr key={product.id} style={{ borderBottom: "1px solid var(--rbw-border)" }}>
                <td className="py-3">
                  <div className="font-bold" style={{ color: "var(--rbw-navy)" }}>{product.brand || product.name}</div>
                  <Muted>{product.category}</Muted>
                </td>
                <td className="text-center py-3">{getPrice("taster")}</td>
                <td className="text-center py-3">{getPrice("pint")}</td>
                <td className="text-center py-3">{getPrice("crowler")}</td>
                <td className="text-center py-3">{getPrice("growler")}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <p className="text-center text-sm mt-6" style={{ color: "var(--rbw-gray-dark)" }}>
        Cash/Debit prices shown. Card prices slightly higher.
      </p>
    </div>
  );
}
